
import React from 'react';
import { User } from '../types';
import { Language, translations } from '../translations';
import { Lock, MessageCircle, ShoppingCart, User as UserIcon, Users, Zap, Gem, Mic } from 'lucide-react';

interface MobileMenuProps {
    user: User | null;
    onNavigate: (view: any) => void;
    lang: Language;
    onLogout: () => void;
    profileIncomplete?: boolean;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ user, onNavigate, lang, onLogout, profileIncomplete = false }) => {
    const t = translations[lang];

    const isFreeUser = !user?.subscription_plan_id && !['admin', 'superadmin', 'vip'].includes(user?.role || '');

    const menuItems: {id: string, icon: React.ReactNode, label: string, desc: string, disabled?: boolean}[] = [
        { id: 'profile', icon: <UserIcon className="w-5 h-5" />, label: t.nav.profile, desc: 'Einstellungen & Stats' },
        { id: 'buddies', icon: profileIncomplete ? <Lock className="w-5 h-5" /> : <Users className="w-5 h-5" />, label: 'Flugbuddies', desc: 'Community & Freunde', disabled: profileIncomplete },
        { id: 'messages', icon: profileIncomplete ? <Lock className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />, label: 'Nachrichten', desc: 'Chats & Gruppen', disabled: profileIncomplete },
        { id: 'market', icon: <ShoppingCart className="w-5 h-5" />, label: 'Marktplatz', desc: 'Gear kaufen & verkaufen', disabled: false }
    ];

    if (user?.role === 'admin' || user?.role === 'superadmin') {
        menuItems.push({ id: 'admin', icon: <Zap className="w-5 h-5 text-amber-500 fill-amber-500/20" />, label: 'Admin', desc: 'Systemverwaltung', disabled: false });
    }

    return (
        <div className="p-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-tight">Menü</h2>

            <div className="grid grid-cols-1 gap-4">
                {menuItems.map(item => {
                    const isLocked = item.disabled;
                    const isProUpgrade = item.id === 'audio_forecast' && isFreeUser;

                    return (
                        <button
                            key={item.id}
                            onClick={() => !isLocked && onNavigate(item.id)}
                            className={`bg-slate-900 border p-4 rounded-2xl flex items-center gap-4 transition-all active:scale-95 text-left group ${isLocked ? 'opacity-50 grayscale cursor-not-allowed' : ''} ${isProUpgrade ? 'border-amber-400/50 shadow-[0_0_15px_rgba(245,158,11,0.15)] ring-1 ring-amber-400/30' : 'border-white/5 hover:border-sky-500/50'}`}
                        >
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-colors ${isProUpgrade ? 'bg-amber-400/10 text-amber-400 group-hover:bg-amber-400/20' : 'bg-slate-800 group-hover:bg-sky-500/20 group-hover:text-sky-400'}`}>
                                {isProUpgrade ? <Gem className="w-5 h-5 text-amber-400" /> : item.icon}
                            </div>
                            <div>
                                <div className="font-bold text-white text-lg flex items-center gap-2">
                                    {item.label}
                                    {isProUpgrade && (
                                        <span className="bg-amber-400 text-slate-950 font-black text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md leading-none">Easy / PRO</span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-500">{item.desc}</div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="pt-8 border-t border-white/5 space-y-4">
                <button
                    onClick={() => window.dispatchEvent(new Event('show-legal-modal'))}
                    className="w-full bg-slate-900 border border-white/5 p-4 rounded-xl font-bold uppercase tracking-widest hover:bg-slate-800 transition-all text-slate-400 hover:text-white"
                >
                    Datenschutz & Impressum
                </button>
                <button
                    onClick={onLogout}
                    className="w-full bg-red-500/10 text-red-500 border border-red-500/20 p-4 rounded-xl font-bold uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all"
                >
                    Logout
                </button>
                <div className="text-center mt-6 text-xs text-slate-600">
                    Streckenfliegen.app Mobile v1.0
                </div>
            </div>
        </div>
    );
};
